import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Layers, Copy } from "lucide-react";
import { Glossary } from "@/components/kit";
import { countryNames } from "@/lib/countries";

const DISCLAIMER = "Not a legal opinion. Kadana provides compliance-ready software — indicative guidance, not certification or legal advice.";

// PATCH 27 Part 5: Compliance Recommendation Panel. Reads the sector x region map (appendix-l)
// against the project's real jurisdiction and the Perimeter-Gate layer marks. Additive to CRX.
const STATUS = {
  relevant: ["Relevant", "k-tag-warn"],
  not_relevant: ["Not relevant", "k-tag-green"],
  unknown: ["Unknown", "k-tag-info"],
};

export default function ComplianceRecommendation({ projectId }) {
  const [rec, setRec] = useState(null);

  const load = useCallback((domain) => {
    const params = domain ? { domain } : {};
    api.get(`/projects/${projectId}/compliance-recommendation`, { params })
      .then((r) => setRec(r.data)).catch(() => {});
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const pickDomain = async (domain) => {
    setRec((r) => ({ ...(r || {}), _saving: true }));
    try { await api.put(`/projects/${projectId}/domain`, { domain }); } catch { /* ignore */ }
    load(domain);
  };

  // SW2 — copy the sector/region/layers/connectors summary as plain text.
  const copyBrief = async () => {
    if (!rec?.has_domain) { toast.error("Pick a sector first."); return; }
    const brief = [
      "Kadana build brief", "",
      `Sector: ${rec.domain_label}`,
      `Scope: ${rec.scope_statement}`,
      `Compliance layers (${rec.layer_count}): ${(rec.layers || []).map((l) => l.name).join(", ") || "—"}`,
      `Connectors likely (${rec.connector_total}): ${(rec.connector_shortlist || []).slice(0, 12).map((c) => c.name).join(", ") || "—"}`,
      "", DISCLAIMER,
    ].join("\n");
    try { await navigator.clipboard.writeText(brief); toast.success("Build brief copied to your clipboard"); }
    catch { toast.error("Couldn't copy automatically — select the text and copy manually."); }
  };

  if (!rec) return null;
  const domains = rec.available_domains || [];

  return (
    <div className="k-container" data-testid="compliance-recommendation" style={{ padding: 22, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Layers size={18} color="var(--accent)" />
          <h3 className="k-heading" style={{ fontSize: 17, margin: 0 }}>Compliance recommendation</h3>
        </div>
        {rec.has_domain && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="k-badge k-badge-accent" data-testid="reco-domain">{rec.domain_label}</span>
            <button className="k-btn k-btn-ghost" data-testid="reco-copy-brief" style={{ padding: "6px 12px", fontSize: 13 }} onClick={copyBrief}>
              <Copy size={13} /> Copy my build brief
            </button>
          </div>
        )}
      </div>

      {/* Sector picker (choose or change the blueprint domain) */}
      <div style={{ marginBottom: 12 }}>
        <div className="k-muted" style={{ fontSize: 12, marginBottom: 6 }}>
          {rec.has_domain ? "Change sector:" : "Pick your app's sector to see what likely applies:"}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {domains.map((d) => (
            <button key={d.domain} data-testid={`reco-pick-${d.domain}`}
              className={`k-badge ${rec.domain === d.domain ? "k-badge-accent" : ""}`}
              style={{ cursor: "pointer" }} onClick={() => pickDomain(d.domain)}>{d.label}</button>
          ))}
        </div>
      </div>

      {!rec.has_domain ? (
        <p className="k-muted" style={{ fontSize: 13 }}>
          Choose a sector above. The panel then lists the compliance layers that apply and a
          connector shortlist scoped to this project's jurisdiction.
        </p>
      ) : (
        <>
          <div className="k-control" data-testid="reco-summary" style={{ fontSize: 13, marginBottom: 12 }}>
            <div className="k-heading" style={{ fontSize: 13, fontWeight: 600 }}>{rec.scope_statement}</div>
            <div className="k-muted" style={{ fontSize: 12, marginTop: 6 }}>
              {rec.connector_total} connector{rec.connector_total === 1 ? "" : "s"} and {rec.layer_count}{" "}
              compliance layer{rec.layer_count === 1 ? "" : "s"} likely apply.
            </div>
            {rec.zero_coverage && (
              <p className="k-tag-warn" data-testid="reco-zero" style={{ fontSize: 12, marginTop: 8, padding: 8, borderRadius: 8 }}>
                No connectors catalogued yet for the selected jurisdiction — your build still gets
                UK/EU-baseline compliance guidance.
              </p>
            )}
          </div>

          {/* Compliance layers by plain-English name + Perimeter-Gate status */}
          <div style={{ marginBottom: 12 }} data-testid="reco-layers">
            <div className="k-heading" style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: "inline-flex", alignItems: "center" }}>
              <Glossary term="Compliance layer">Compliance layers</Glossary>
            </div>
            {rec.layers?.length ? rec.layers.map((l) => {
              const [label, cls] = STATUS[l.status] || STATUS.unknown;
              return (
                <div key={l.id} className="k-control" style={{ marginBottom: 6 }} data-testid={`reco-layer-${l.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <span className="k-heading" style={{ fontSize: 13 }}>{l.name}</span>
                    <span className={cls} style={{ fontSize: 11, fontWeight: 600 }}>{label}</span>
                  </div>
                  <div className="k-muted" style={{ fontSize: 11, marginTop: 3 }}>{l.note} · {l.source}</div>
                </div>
              );
            }) : <p className="k-muted" style={{ fontSize: 12 }}>No default layers mapped for this sector.</p>}
          </div>

          {/* Blueprints */}
          {rec.blueprints?.length > 0 && (
            <div style={{ marginBottom: 12 }} data-testid="reco-blueprints">
              <div className="k-heading" style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Relevant blueprints</div>
              {rec.blueprints.map((b) => (
                <div key={b.id} className="k-control" style={{ marginBottom: 6 }} data-testid={`reco-bp-${b.id}`}>
                  <div className="k-heading" style={{ fontSize: 13 }}>{b.id} · {b.name} <span className="k-badge">phase {b.phase}</span></div>
                  <div className="k-muted" style={{ fontSize: 11, marginTop: 3 }}>{(b.jur || []).join(", ")} · {b.note}</div>
                </div>
              ))}
            </div>
          )}

          {/* Connector + KYC shortlist */}
          {rec.connector_shortlist?.length > 0 && (
            <div style={{ marginBottom: 12 }} data-testid="reco-connectors">
              <div className="k-heading" style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                Connector shortlist ({rec.connector_shortlist.length} shown)
              </div>
              {rec.connector_shortlist.slice(0, 8).map((c) => (
                <div key={c.id} className="k-control" style={{ marginBottom: 6 }} data-testid={`reco-conn-${c.id}`}>
                  <div className="k-heading" style={{ fontSize: 13 }}>{c.name}</div>
                  <div className="k-muted" style={{ fontSize: 11 }}>
                    {countryNames(c.jurisdiction || [])} · {c.sector} · class {c.access_class} · {c.status}
                    {!c.access_route_verified && " · access route unverified"}
                  </div>
                </div>
              ))}
            </div>
          )}
          {rec.kyc_shortlist?.length > 0 && (
            <div style={{ marginBottom: 12 }} data-testid="reco-kyc">
              <div className="k-heading" style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>KYC / identity providers</div>
              {rec.kyc_shortlist.slice(0, 6).map((c) => (
                <span key={c.id} className="k-badge" data-testid={`reco-kyc-${c.id}`} style={{ marginRight: 6, marginBottom: 6 }}>{c.name}</span>
              ))}
            </div>
          )}

          <p className="k-muted" data-testid="reco-legal" style={{ fontSize: 12 }}>{rec.legal_templates?.note}</p>
          <p className="k-tag-warn" data-testid="reco-inline-disclaimer" style={{ fontSize: 11.5, marginTop: 8, padding: 8, borderRadius: 8, lineHeight: 1.5 }}>
            {DISCLAIMER}
          </p>
          <p className="k-muted" style={{ fontSize: 11, marginTop: 6 }}>{rec.note}</p>
        </>
      )}
    </div>
  );
}
